import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getTargets, createTarget, updateTarget, deleteTarget } from '../../services/api';

const ManageTargets = () => {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', notes: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadTargets();
  }, []);

  const loadTargets = async () => {
    try {
      const response = await getTargets();
      setTargets(response.data);
    } catch (err) {
      setError('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', notes: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (target) => {
    setFormData({ name: target.name, notes: target.notes || '' });
    setEditingId(target._id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      if (editingId) {
        await updateTarget(editingId, formData);
      } else {
        await createTarget(formData);
      }
      resetForm();
      loadTargets();
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel salvataggio');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (targetId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa persona?')) return;

    try {
      await deleteTarget(targetId);
      loadTargets();
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nell\'eliminazione');
    }
  };

  if (loading) return <div className="loading">Caricamento...</div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <Link to="/admin" className="back-link">← Torna al pannello admin</Link>
      </div>

      <div className="page-header-flex">
        <h1>Gestione Persone da Visitare</h1>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="btn btn-primary">
          {showForm && !editingId ? 'Annulla' : 'Nuova persona'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <form onSubmit={handleSubmit} className="admin-form">
          <h3>{editingId ? 'Modifica persona' : 'Nuova persona'}</h3>

          <div className="form-group">
            <label htmlFor="name">Nome</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="notes">Note (opzionale)</label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="3"
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Salvataggio...' : (editingId ? 'Salva modifiche' : 'Crea')}
            </button>
            {editingId && (
              <button type="button" onClick={resetForm} className="btn btn-secondary">
                Annulla
              </button>
            )}
          </div>
        </form>
      )}

      {targets.length === 0 ? (
        <p className="empty-state">Nessuna persona nella lista</p>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Note</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {targets.map((target) => (
                <tr key={target._id}>
                  <td data-label="Nome">{target.name}</td>
                  <td className="notes-cell" data-label="Note">{target.notes || '-'}</td>
                  <td className="actions-cell">
                    <button
                      onClick={() => handleEdit(target)}
                      className="btn btn-small btn-secondary"
                    >
                      Modifica
                    </button>
                    <button
                      onClick={() => handleDelete(target._id)}
                      className="btn btn-danger btn-small"
                    >
                      Elimina
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ManageTargets;
